import { useId } from 'react'

import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import {
  type BoxProps,
  RadioGroupItem as EcRadioGroupItem,
  RadioGroupItemText as EcRadioGroupItemText,
  HStack,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
} from '@enechain/ecloud-designsystem'

import { TradeSideLabel } from '~/components/common/TradeSideLabel'

// chakra v3.29 + React 18 types の既知不具合で `RadioGroupItemProps` /
// `RadioGroupItemTextProps` (= `extends HTMLChakraProps<...>, UnstyledProp`) が
// `{ unstyled }` のみに collapse し、children を含む全 props を JSX で受け取れなくなる。
// ecloud 由来の component をそのまま使い、取りこぼした props を BoxProps で型付けし直す
// (`pages/BalanceImputationPolicy/components/DemandMethodRadio.tsx` と同じ回避)。
const RadioGroupItem = EcRadioGroupItem as React.FC<
  BoxProps & { value: string }
>
const RadioGroupItemText = EcRadioGroupItemText as React.FC<BoxProps>

type Props<T extends string> = {
  /** 択一群の accessible name (= フィルタ名。例: 「売買」「売/買」)。 */
  ariaLabel: string
  /** 同一画面に複数の radio group が並ぶため name で分離する。 */
  name: string
  /**
   * 中立選択肢のラベル (例: 「すべて」)。色は付けない。
   * **省略すると中立選択肢を出さない** = 買 / 売 の 2 択になる
   * (入力フォームの必須項目向け。 フィルタは 3 択のまま)。
   */
  allLabel?: string
  /**
   * 呼び出し側の state が使う値 (画面ごとに '' / 'all' / Side 数値文字列)。
   * `all` は 3 択の時だけ要る。
   */
  values: { all?: T; buy: T; sell: T }
  /** 無効化 (表示専用モード)。 */
  isDisabled?: boolean
  value: T
  onChange: (next: T) => void
}

/**
 * 一覧フィルタの「売買」を すべて / 買 / 売 の 3 択 radio で表す共通部品。
 * 買 / 売 は一覧の行バッジ・入力フォームと同じ `TradeSideLabel`
 * (DS `TradeLabel` = `Object.bid` / `Object.offer` の色付き pill) で示し、
 * 中立の「すべて」は素テキストのまま置く
 * (design 8080 の 2026-07-28 統一。`.ecl-trade-label--buy/--sell` 相当)。
 *
 * ## [DS 差異] `RadioButton` ではなく radio-group の slot を直接使う
 *
 * DS `RadioButton` の `radios[].label` は **`string` 固定**で ReactNode を取れず、
 * pill をラベル位置に置けない。そこで `RadioButton` が内部で使っているのと
 * **同じ slot** (`RadioGroupRoot` / `Item` / `ItemControl` / `ItemText`) を直接組む
 * (`DemandMethodRadio` / `IntradayBidCreateModal` と同じ回避)。
 * 見た目は DS `RadioButton` の実装 (`flexWrap="wrap"` / `gap={4}` の行並び) に揃える。
 * DS `RadioButton` の `label` が ReactNode を取れるようになれば畳んで戻せる。
 *
 * ## 色分けの不変条件
 *
 * - 買 = `Object.bid` / 売 = `Object.offer` の**濃い単色地 + 白文字の pill**。
 *   ラベル文字だけを着色する形 (`Text.bid` 等) は DS・design ともに不可。
 * - 「すべて」= 中立選択肢なので着色しない (pill にしない)。
 *
 * accessible name は `TradeSideLabel` の `aria-label` (`common:tradeSide.*`) 由来に
 * なるため、ja では 買 / 売 のまま変わらない。
 *
 * ## `name` を実体ごとに一意化する理由
 *
 * DS `SideMenuLayout` は**ページ全体をモバイル用にもう 1 組 `display: none` で描画する**。
 * そのため呼び出し側が渡した `name` をそのまま native radio (`RadioGroupItemHiddenInput`)
 * に流すと、 同名の radio group が document 内に 2 組できる。 native radio group は
 * 「同名で checked は 1 つだけ」なので、 可視側を選択した瞬間に非表示側の `checked` が
 * 外れる / その逆が起き、 **可視側から checked が消える**。 見た目 (選択マーク) は
 * ark-ui が state 由来で描くため正しく見え、 **目視では気付けない**
 * (`AccessibleSegmentedControl` の JSDoc に同じ不具合の実測記録あり)。
 *
 * `useId()` は同じ component でも実体ごとに別の値を返すため、 複製された 2 組が
 * 別グループになり干渉しなくなる。 呼び出し側から見た `name` の意味は変わらない。
 */
export const TradeSideRadioGroup = <T extends string>({
  ariaLabel,
  name,
  allLabel,
  values,
  value,
  onChange,
  isDisabled = false,
}: Props<T>): React.ReactElement => {
  const instanceId = useId()

  return (
    <RadioGroupRoot
      aria-label={ariaLabel}
      disabled={isDisabled}
      name={`${name}-${instanceId}`}
      onValueChange={(details: { value: string | null }) => {
        // 中立選択肢がある画面では「解除 = すべて」。 2 択の画面 (入力フォーム) は
        // 解除相当の値を持たないので、 null は現在値のままにする。
        const next = details.value ?? values.all
        if (next === undefined) return
        onChange(next as T)
      }}
      value={value}
    >
      {/* DS `RadioButton` の内部レイアウト (flexWrap="wrap" / gap=4) と同値。 */}
      <HStack alignItems="center" flexWrap="wrap" gap={4}>
        {allLabel !== undefined && values.all !== undefined && (
          <RadioGroupItem alignItems="center" value={values.all}>
            <RadioGroupItemHiddenInput />
            <RadioGroupItemControl />
            <RadioGroupItemText>{allLabel}</RadioGroupItemText>
          </RadioGroupItem>
        )}
        <RadioGroupItem alignItems="center" value={values.buy}>
          <RadioGroupItemHiddenInput />
          <RadioGroupItemControl />
          <RadioGroupItemText>
            {/* `as="span"`: `RadioGroupItemText` は `<span>`・`RadioGroupItem` は
                `<label>` (どちらも phrasing content しか置けない) なので、
                DS 既定の `<p>` のままだと入れ子が不正になる。 */}
            <TradeSideLabel as="span" side={Side.BUY} />
          </RadioGroupItemText>
        </RadioGroupItem>
        <RadioGroupItem alignItems="center" value={values.sell}>
          <RadioGroupItemHiddenInput />
          <RadioGroupItemControl />
          <RadioGroupItemText>
            <TradeSideLabel as="span" side={Side.SELL} />
          </RadioGroupItemText>
        </RadioGroupItem>
      </HStack>
    </RadioGroupRoot>
  )
}
